import { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, Chip,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

/**
 * Configuración del correo de envío al cliente (shortlist ATS) del proyecto activo.
 * Variables soportadas: {{cargo}}, {{empresa}}, {{candidatos}} (lista de candidatos
 * con enlaces a CV y resumen ejecutivo; si no se incluye, la tabla se anexa al final).
 */
const AtsShortlistEmailTab = () => {
  const { t } = useTranslation();
  const { organization, project, authToken, showAlert } = useStore.getState();

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!organization || !project) { setLoading(false); return; }
      try {
        setLoading(true);
        const res = await api.get(`/projects/ats-shortlist-email`, {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = res.data.response?.data;
        if (data) {
          setSubject(data.subject || '');
          setBody(data.body || '');
        }
      } catch (err) {
        console.error(err);
        showAlert(t('ats.config.shortlistEmail.loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await api.put(
        `/projects/ats-shortlist-email`,
        { subject, body },
        {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (res.data.response?.status_response === 'OK') {
        showAlert(t('ats.config.shortlistEmail.saveSuccess'), 'success');
      } else {
        showAlert(t('ats.config.shortlistEmail.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.config.shortlistEmail.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        {t('ats.config.shortlistEmail.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ats.config.shortlistEmail.description')}
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {t('ats.config.shortlistEmail.variables')}:
        </Typography>
        <Chip label="{{cargo}}" size="small" variant="outlined" />
        <Chip label="{{empresa}}" size="small" variant="outlined" />
        <Chip label="{{candidatos}}" size="small" variant="outlined" />
      </Box>

      <TextField
        fullWidth
        label={t('ats.config.shortlistEmail.subject')}
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        multiline
        minRows={8}
        label={t('ats.config.shortlistEmail.body')}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={saving}
        sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
      >
        {t('ats.config.shortlistEmail.save')}
      </Button>
    </Box>
  );
};

export default AtsShortlistEmailTab;
